import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { get, type Booking, type BookingList } from "../api.js";
import { formatDate, formatTime, isoDay } from "../format.js";
import { Pagination, usePagination } from "../Pagination.js";
import { useAsync } from "../useAsync.js";
import type { StudentEngagement } from "@wmf/shared/studentEngagement";
import { NoteActivityStatus } from "../engagement/ActivityPanels.js";
import "../studentRecordNotes.css";
import { CARD_RECORD_PAGE_SIZE } from "../paginationPageSizes.js";

export type StudentNoteState = "No note" | "Draft" | "Published";

/** The booking mirror already owns these three note states. */
export function studentNoteState(
  entry: Pick<Booking, "lessonId" | "lessonPublishedAt">,
): StudentNoteState {
  if (entry.lessonPublishedAt !== null) return "Published";
  return entry.lessonId === null ? "No note" : "Draft";
}

export function studentNoteActionLabel(state: StudentNoteState): string {
  if (state === "No note") return "Write note";
  if (state === "Draft") return "Continue note";
  return "View / edit note";
}

/**
 * Keep only this student's completed/past rows and put the newest first. The
 * server now applies the student filter before its bounded result limit; this
 * client filter remains as a defensive check on the response contract.
 */
export function recentStudentNoteBookings(
  bookings: readonly Booking[],
  studentId: string,
  now = Date.now(),
): Booking[] {
  return bookings
    .filter((booking) => {
      const startsAt = Date.parse(booking.startsAt);
      return booking.studentId === studentId && Number.isFinite(startsAt) && startsAt <= now;
    })
    .sort((a, b) => b.startsAt.localeCompare(a.startsAt));
}

/**
 * `useAsync` intentionally preserves the last good payload if a refresh fails.
 * Note-state badges must not present that stale payload as current.
 */
export function reliableStudentNoteBookings(
  bookings: readonly Booking[],
  studentId: string,
  error: string | null,
  now = Date.now(),
): Booking[] {
  return error === null ? recentStudentNoteBookings(bookings, studentId, now) : [];
}

function noteStateClass(state: StudentNoteState): string {
  if (state === "Published") return "good";
  if (state === "Draft") return "warn";
  return "quiet";
}

interface Props {
  studentId: string;
  studentName: string;
}

export function StudentRecordNotes({ studentId, studentName }: Props) {
  const location = useLocation();
  const recent = useAsync(
    () =>
      get<BookingList>("/admin/bookings", {
        from: isoDay(-120),
        to: isoDay(0),
        studentId,
      }),
    [studentId],
    { reloadOnMirrorRefresh: true },
  );

  const bookings = useMemo(
    () =>
      reliableStudentNoteBookings(
        recent.data?.bookings ?? [],
        studentId,
        recent.error,
      ),
    [recent.data?.bookings, recent.error, studentId],
  );
  const paginated = usePagination(bookings, CARD_RECORD_PAGE_SIZE, studentId);
  const noteIds = paginated.items.filter((booking) => studentNoteState(booking) === "Published" && booking.lessonId).map((booking) => booking.lessonId).join(",");
  const activity = useAsync(async () => ({ studentId, noteIds, activity: noteIds ? await get<StudentEngagement>(`/engagement/students/${studentId}`, { noteIds }) : { notes: [] } }), [studentId, noteIds]);
  const unfinished = bookings.filter((booking) => studentNoteState(booking) !== "Published");
  const returnTo = `${location.pathname}${location.search}#lesson-notes`;

  return (
    <section
      className="student-note-workspace"
      id="lesson-notes"
      aria-labelledby="student-note-heading"
    >
      <div className="student-record-section-head">
        <div>
          <h2 id="student-note-heading">Lesson notes</h2>
          <p className="small muted">
            Recent note status for {studentName}, newest first. Write, finish or review a note
            without leaving this student&rsquo;s Lessons workspace behind.
          </p>
        </div>
        {!recent.loading && !recent.error && unfinished.length > 0 && (
          <span className="tag warn">
            {unfinished.length} {unfinished.length === 1 ? "note needs" : "notes need"} attention
          </span>
        )}
      </div>

      {recent.error && (
        <div className="notice">
          <p className="small">
            Recent note status could not be refreshed, so stale note states are hidden. Lesson
            history below is still available.
          </p>
        </div>
      )}

      <div className="panel student-note-recent">
        <div className="student-note-section-title">
          <h3>Recent lessons</h3>
          <span className="small muted">Last 120 days</span>
        </div>

        {recent.loading && <p className="empty">Loading recent note status…</p>}
        {!recent.loading && !recent.error && bookings.length === 0 && (
          <p className="empty">No recent lessons are available for note writing.</p>
        )}

        {!recent.loading && !recent.error && bookings.length > 0 && (
          <>
            <div className="student-note-list">
              {paginated.items.map((booking) => {
                const state = studentNoteState(booking);
                const activityLoaded = activity.data?.studentId === studentId && activity.data.noteIds === noteIds;
                const noteActivity = activityLoaded && !activity.error
                  ? activity.data!.activity.notes?.find((note) => note.id === booking.lessonId) : undefined;
                return (
                  <article className="student-note-row" key={booking.bookingId}>
                    <div className="student-note-date">
                      <strong>{formatDate(booking.startsAt)}</strong>
                      <span className="small muted">{formatTime(booking.startsAt)}</span>
                    </div>
                    <div className="student-note-main">
                      <div className="student-note-heading-line">
                        <strong>{booking.serviceName ?? booking.instrument ?? "Lesson"}</strong>
                        <span className={`tag ${noteStateClass(state)}`}>{state}</span>
                      </div>
                      <div className="small muted">
                        {[booking.tutorName, booking.attendanceStatus].filter(Boolean).join(" · ") ||
                          "Lesson record"}
                      </div>
                      {state === "Published" && <NoteActivityStatus activity={noteActivity} loading={activity.loading} error={activity.error} loaded={activityLoaded} />}
                    </div>
                    <Link
                      className="student-note-action"
                      to={`/bookings/${encodeURIComponent(booking.bookingId)}/lesson`}
                      state={{
                        returnTo,
                        returnLabel: `${studentName} lessons`,
                      }}
                    >
                      {studentNoteActionLabel(state)}
                    </Link>
                  </article>
                );
              })}
            </div>
            <Pagination
              page={paginated.page}
              pages={paginated.pages}
              onPrevious={paginated.previous}
              onNext={paginated.next}
              ariaLabel="Recent lessons pagination"
            />
          </>
        )}
      </div>
    </section>
  );
}
